import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { GridProps } from '@mui/material';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import type {
  DataSeriesType,
  UpstreamApiDashboardData,
  WidgetConfig,
} from 'src/types/dashboard';
import { numberOrDash } from 'src/helper/helper';
import SpeedometerChart from 'src/components/dashboard/charts/SpeedometerChart';
import NumberDifference from 'src/components/dashboard/DashboardPage/components/NumberDifference';
import { useSpeedometerReData } from 'src/components/dashboard/DashboardPage/widgets/SpeedometerReWidget/useSpeedometerReData';
import useQuestionData from 'src/components/dashboard/hooks/useQuestionData';

import { styles } from './WidgetViewCommonWidget.styles';

export const SPEEDOMETER_WIDGET_TEST_ID = 'speedometer-widget';

export interface WidgetViewSpeedometerWidgetProps extends GridProps {
  data: UpstreamApiDashboardData;
  widgetConfig: WidgetConfig;
}

const WidgetViewSpeedometerReWidget: FC<WidgetViewSpeedometerWidgetProps> = ({
  data,
  widgetConfig,
}) => {
  const diagram = (widgetConfig.dataseries?.content as DataSeriesType)
    ?.diagram?.[0];
  const theme = useTheme();
  const upXl = useMediaQuery(theme.breakpoints.up('xl'), { noSsr: true });
  const { t } = useTranslation('followUp');

  const { comparisonDataName, comparisonAggregate, focalAggregate } =
    useQuestionData(
      widgetConfig.comparison_type,
      widgetConfig.questions[0] ?? '',
      data,
    );

  const { totalValue, yoyDiffAttribute, comparisonAttribute } =
    useSpeedometerReData({
      diagram,
      focalAggregate,
      comparisonAggregate,
    });

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={styles.wrapper}
      data-test-id={SPEEDOMETER_WIDGET_TEST_ID}
    >
      <Stack
        direction="column"
        spacing={1}
        sx={{
          justifyContent: 'space-between',
          height: '100%',
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'center',
          }}
        >
          <SpeedometerChart
            percentage={totalValue ? totalValue / 100 : 0}
            height={280}
            color={diagram?.color}
          >
            <Typography
              component="span"
              sx={{
                fontSize: upXl ? '2.75rem' : '2.25rem',
                fontWeight: 600,
                lineHeight: 'normal',
                position: 'relative',
                mb: 0,
              }}
            >
              {numberOrDash(totalValue, !!diagram?.show_percent)}
            </Typography>
            <Typography variant="body3" component="div">
              <NumberDifference
                comparisonDiff={
                  typeof yoyDiffAttribute === 'number'
                    ? Math.round(yoyDiffAttribute)
                    : null
                }
                showArrow
                centerEmpty
              />
            </Typography>
          </SpeedometerChart>
        </Stack>
        <Box
          sx={{
            pb: 2,
          }}
        >
          <Box sx={styles.headline}>{focalAggregate?.label || '--'}</Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
              mt: 1,
              justifyContent: 'center',
            }}
          >
            <Typography variant="body3">
              {comparisonDataName || t('Labels.Benchmark')}
            </Typography>
            <Typography variant="body4">
              {numberOrDash(comparisonAttribute, !!diagram?.show_percent)}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};
export default WidgetViewSpeedometerReWidget;
